import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import queryString from 'query-string';
import { useMediaQuery } from 'react-responsive';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
import { Container, Row, Column, List, Modal, Button, Icon } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA } from '../../components';
import { getSessionStorage } from '../../components/utilities/functions';
import { isValidArray } from '../../components/utilities/validations';
import DoNotContact from '../../components/page/dnc/do-not-contact';
import ServiceRequest from '../../components/common/forms/service-request';
import FAQ from '../../components/common/faq';
import FAQDynamic from '../../components/common/faq-dynamic';
import DynamicComponent from '../../components/common/dynamic-component';
import EmailPreferenceForm from '../../components/common/forms/email-pref-form';
import VisualJourneyModal from '../../components/page/support/visual-journey-modal';
import CardSection from '../../components/common/card-section';
import CTARibbon from '../../components/common/cta-ribbon';
import ShippingReturnsForm from '../../components/common/forms/shipping-return-form';
import CCPAForm from '../../components/common/forms/ccpa-form';
import ContactUs from '../../components/common/forms/contact-us';
import styles from './index.module.scss';

const Support = (props) => {
	const [activeTab, setActiveTab] = useState(null);
	const [dynamic, setDynamic] = useState({ faq: true });
	const [modal, setModal] = useState({});
	const [journeyOptOut, setJourneyOptOut] = useState(false);
	const isTablet = useMediaQuery({ query: '(min-width: 790px)' });
	const pageData = props.data.contentstackSupport;
	const utm = queryString.parse(props.location.search);
	const path = props.location.origin + props.location.pathname;
	const hideLeadGen = ['/support/california-rights-policy/data-privacy/', '/support/submit-question/'].includes(pageData.url);

	useEffect(() => {
		if (getSessionStorage('journeyOptOut')) {
			setJourneyOptOut(true);
		} else {
			const serviceLink = document.querySelector('#js-supportCards .sash-Column:nth-child(1) a');
			if (serviceLink) {
				serviceLink.addEventListener('click', (e) => onModalCtaClick(e, null, null, 'service'));
				return () => serviceLink.removeEventListener('click', (e) => onModalCtaClick(e, null, null, 'service'));
			}
		}
	}, []);

	const setBackgroundColor = (color) => {
		switch (color) {
			case 'light gray':
				return 'bg-neutral-50';
			default:
				return 'bg-white';
		}
	};

	const onTabClick = (index) => {
		if (activeTab == index) {
			setActiveTab(null);
		} else {
			setActiveTab(index);
		}
	};

	const renderSubLinks = (block) => (
		<div className='border-t-4 border-black bg-white px-7 py-4 lg:px-10'>
			<Row verticalGutters={true}>
				{isValidArray(block.link_group.group[activeTab].group) &&
					block.link_group.group[activeTab].group.map((item, index) => {
						const isExternal = item.link.href.includes('http');

						return (
							<Column
								key={index}
								xs={12}
								sm={6}
								lg={4}
							>
								{item.link.title === 'Schedule a Service Visit' && !journeyOptOut ? (
									<button
										className='block text-lg font-semibold no-underline md:text-xl xl:text-2xl [&>span]:flex [&>span]:items-center'
										onClick={(e) => onModalCtaClick(e, null, null, 'service')}
									>
										{item.link.title}
										<Icon name='keyboard_arrow_right' />
									</button>
								) : (
									<CTA
										data={{
											link: { href: item.link.href },
											target: isExternal ? '_blank' : '_self',
											title: isExternal ? 'Link opens in a new window' : null,
										}}
										renderAs='link'
										className='block text-lg font-semibold no-underline md:text-xl xl:text-2xl [&>span]:flex [&>span]:items-center'
									>
										{item.link.title}
										<Icon name='keyboard_arrow_right' />
									</CTA>
								)}
								{item.description && (
									<div
										className='text-subtitle-2 font-normal'
										dangerouslySetInnerHTML={{
											__html: item.description,
										}}
									/>
								)}
							</Column>
						);
					})}
			</Row>
		</div>
	);

	const checkDynamic = (name, available) => {
		setDynamic({ ...dynamic, [name]: available });
	};

	const renderFaqColumn = (group, index, isSingle) => (
		<div
			key={index}
			className='mb-8 lg:mb-12'
		>
			{isValidArray(group?.faqs) && (
				<>
					{group.subheading && <h3 className='s2-h3__alt'>{group.subheading}</h3>}
					<FAQ
						data={group.faqs}
						noStructuredData={true}
						multipleColumns={isSingle}
					/>
				</>
			)}

			{isValidArray(group?.dynamic) && (
				<>
					{group.subheading && <h3 className='s2-h3__alt'>{group.subheading}</h3>}
					<FAQDynamic
						dynamic={group.dynamic[0]}
						hideStructuredData={true}
						multipleColumns={isSingle}
						checkDynamic={checkDynamic}
						hasContainer={false}
					/>
				</>
			)}

			{isValidArray(group.ctas) && (
				<div className='mt-4 text-center'>
					{group.ctas[0].gtm_link_type !== 'button' ? (
						<CTA data={group.ctas[0]}>
							{group.ctas[0].link.title}
							<Icon name='keyboard_arrow_right' />
						</CTA>
					) : (
						<CTA data={group.ctas[0]} />
					)}
				</div>
			)}
		</div>
	);

	const renderIWCCard = (iwc, block) => (
		<Row
			verticalGutters={true}
			reverse={block.image_position === 'right'}
		>
			{iwc.file?.url && (
				<Column
					xs={12}
					md={block.image_position === 'top' ? 12 : 'auto'}
				>
					<Image
						data={{ image: iwc.file }}
						className='mx-auto !block'
						objectFit='contain'
					/>
				</Column>
			)}
			<Column xs='fluid'>
				<div className='text-center md:text-left'>
					{iwc.heading && <h3 className='mb-2.5 text-xl font-normal xl:text-3xl'>{iwc.heading}</h3>}
					{iwc.body && (
						<div
							dangerouslySetInnerHTML={{ __html: jsonToHtml(iwc.body) }}
							className='mb-5'
						/>
					)}

					{isValidArray(iwc.cta) && (
						<List
							bullets='none'
							layout='inline'
							className='justify-center md:justify-start'
						>
							{iwc.cta.map((link, index) => (
								<li
									key={index}
									className='mr-2.5 mt-2.5'
								>
									<CTA data={link} />
								</li>
							))}
						</List>
					)}
				</div>
			</Column>
		</Row>
	);

	const onModalCtaClick = async (e, image, heading, open) => {
		e.preventDefault();
		setModal({ open: open, image: image, heading: heading });
	};

	const onModalCloseClick = () => setModal({});

	const [formState, setFormState] = useState('');
	const handleFormStateChange = (newState) => {
		setFormState(newState);
	};

	return (
		<Layout
			id='template-Support'
			hideLeadGen={hideLeadGen}
			pageTitle={pageData.seo?.title}
		>
			{pageData.hero_heading && (
				<div className='bg-black'>
					{pageData.breadcrumb && (
						<Breadcrumbs
							data={pageData.breadcrumb}
							currentUrl={pageData.url}
							className='[&_a]:text-white [&_a]:decoration-white [&_li]:text-white'
						/>
					)}
					<Container>
						<h1 className='m-0 py-12 text-white md:py-16'>{pageData.hero_heading}</h1>
						{pageData.hero_subheading && (
							<div
								className={'text-white'}
								dangerouslySetInnerHTML={{ __html: pageData.hero_subheading }}
							/>
						)}
					</Container>
				</div>
			)}

			{isValidArray(pageData?.page_blocks) &&
				pageData.page_blocks.map((block, index) => {
					if (isValidArray(block?.columns?.column)) {
						return (
							<div
								key={index}
								id='supportColumns'
								className={`py-8 md:py-10 xl:py-14 ${setBackgroundColor(block.columns.background)}`}
							>
								<Container>
									{block.columns.heading && <h2 className='mb-5 text-center xl:mb-8'>{block.columns.heading}</h2>}

									<Row
										className='text-center xl:-mx-8'
										verticalGutters={true}
									>
										{block.columns.column.map((col, index) => (
											<Column
												className={`!py-0 ${col.theme == 'returns2' ? 'md:mb-0' : ''} ${col.theme == 'returns' ? 'mb-0' : 'mb-10'}`}
												xs={col.column_mobile}
												md={col.column_desktop}
												key={index}
											>
												<div className={`${setBackgroundColor(col.background)} ${col.theme ? styles[`supportColumn_${col.theme}`] : ''} ${col.justify ? `text-${col.justify}` : ''} ${col.theme == 'returns' ? 'border border-neutral-200 p-4 md:p-6 lg:p-8' : ''} ${col.theme == 'returns2' ? 'justify-center md:flex md:h-full md:flex-col' : ''}`}>
													{col.heading && <h2 className={`${col.theme == 'returns' ? 'text-h3 mb-4 text-center md:text-left xl:mb-6' : ''}`}>{col.heading}</h2>}
													{col.description && (
														<div
															className={`${col.theme == 'returns' ? 'returns-target-span [&>p]:text-subtitle first:[&>p>span]:mr-1 first:[&>p>span]:text-4xl first:[&>p>span]:text-neutral-400 last:[&>p>span]:ml-2 last:[&>p>span]:font-bold [&>p]:mb-4 [&>p]:flex [&>p]:items-center [&>p]:justify-center [&>p]:text-black md:[&>p]:justify-start md:[&>p]:pl-1 xl:[&>p]:mb-6 [&>ul]:my-0 [&>ul]:pl-6' : ''}`}
															dangerouslySetInnerHTML={{ __html: col.description }}
														/>
													)}
													{isValidArray(col.ctas) &&
														col.ctas.map((btn, index) => (
															<div key={index}>
																{btn.gtm_link_type === 'button' && (
																	<CTA
																		data={btn}
																		className={`mr-2.5 mt-2.5 ${styles.supportColumns_TextLink}`}
																	/>
																)}
																{btn.gtm_link_type === 'text_link' && (
																	<CTA data={btn}>
																		{btn.image && btn.image[0] && <Image data={btn.image[0]} />}
																		<span>{btn.link.title}</span>
																	</CTA>
																)}
															</div>
														))}
												</div>
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.forms != null) {
						return (
							<div
								key={index}
								id='supportForms'
								className={`${block.forms.form_type === 'return' ? 'mb-8 md:mb-10 xl:mb-14' : 'py-8 md:py-10 xl:py-14'}`}
							>
								<Container>
									<Row
										reverse={block.forms.form_position === 'left'}
										justify={block.forms.form_position === 'full' ? 'center' : ''}
									>
										{block.forms.aside_content && (
											<Column
												xs={12}
												lg={block.forms.form_position === 'full' ? 12 : 7}
											>
												{block.forms.aside_content.heading && <h2 className='mb-8'>{block.forms.aside_content.heading}</h2>}
												{block.forms.aside_content.description && (
													<div
														dangerouslySetInnerHTML={{
															__html: block.forms.aside_content.description,
														}}
														className='mb-8'
													/>
												)}
												{block.forms.aside_content.subheading && <h3 className='mb-8'>{block.forms.aside_content.heading}</h3>}
											</Column>
										)}
										{(block.forms.form_type === 'DNC' || block.forms.form_type === 'Service') && (
											<Column
												xs={12}
												lg={block.forms.form_position === 'full' ? 8 : 'fluid'}
												className='lg:mx-auto'
											>
												{block.forms.form_type === 'DNC' && <DoNotContact data={block.forms.aside_content} />}
												{block.forms.form_type === 'Service' && (
													<div className='sticky top-[70px] m-auto max-w-[600px] bg-primary p-7 lg:py-10'>
														{block.forms.heading && <h3>{block.forms.heading}</h3>}
														{block.forms.subheading && <p className='mb-7 text-center'>{block.forms.subheading}</p>}
														<ServiceRequest
															path={path}
															utm={utm}
														/>
													</div>
												)}
											</Column>
										)}
										{block.forms.form_type === 'Email' && (
											<Column
												xs={12}
												lg={block.forms.form_position === 'full' ? 12 : 'fluid'}
											>
												<div>
													{block.forms.heading && <p className='pb-2.5 text-xl sm:text-2xl md:text-4xl'>{block.forms.heading}</p>}
													<EmailPreferenceForm data={block.forms} />
												</div>
											</Column>
										)}
										{block.forms.form_type === 'return' && (
											<Column
												xs={12}
												md={8}
											>
												{block.forms.heading && <h2 id='returnForm'>{block.forms.heading}</h2>}
												{block.forms.subheading && <p>{block.forms.subheading}</p>}
												<ShippingReturnsForm
													order={props?.location?.state}
													formState={handleFormStateChange}
												/>
											</Column>
										)}
										{block.forms.form_type === 'ccpa' && (
											<Column
												xs={12}
												md={8}
											>
												{block.forms.heading && <h2 id='ccpaForm'>{block.forms.heading}</h2>}
												{block.forms.subheading && <p>{block.forms.subheading}</p>}
												<CCPAForm />
											</Column>
										)}
										{block.forms.form_type === 'contact_us' && (
											<Column
												xs={12}
												md={8}
											>
												{block.forms.heading && <h2 id='contactForm'>{block.forms.heading}</h2>}
												{block.forms.subheading && <p>{block.forms.subheading}</p>}
												<ContactUs />
											</Column>
										)}
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.image_with_content?.group)) {
						return (
							<div
								className='py-8 md:py-12 xl:py-16'
								key={index}
							>
								<Container>
									{block.image_with_content.heading && <h2 className='mb-5 text-center font-semibold xl:mb-8'>{block.image_with_content.heading}</h2>}
									<Row
										verticalGutters={true}
										largeGutters={block.image_with_content.style !== 'resource'}
									>
										{block.image_with_content.group.map((iwc, index) => (
											<Column
												key={index}
												xs={block.image_with_content.column_mobile}
												lg={block.image_with_content.column_desktop}
											>
												{renderIWCCard(iwc, block.image_with_content)}
											</Column>
										))}
									</Row>
								</Container>
							</div>
						);
					}

					if (block.general_content != null && block.general_content.heading === 'Return Form') {
						return (
							<div
								key={index}
								className={`pb-6 pt-8 md:pt-12 xl:pt-16 ${setBackgroundColor(block.general_content.background_color)} ${block.general_content.align === 'center' ? 'text-center' : ''}`}
							>
								<Container>
									<Row justify={'center'}>
										<Column
											xs={12}
											md={8}
										>
											{block.general_content.heading && (
												<h2
													id='generalContentHeading'
													className={`${formState != 'success' ? 'mb-3' : 'mb-0'}`}
												>
													{block.general_content.heading}
												</h2>
											)}
											{block.general_content.description && formState != 'success' && <div dangerouslySetInnerHTML={{ __html: block.general_content.description }} />}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.general_content != null && block.general_content.heading !== 'Return Form') {
						return (
							<div
								key={index}
								className={`py-8 md:py-12 xl:py-16 ${setBackgroundColor(block.general_content.background_color)} ${block.general_content.align === 'center' ? 'text-center' : ''}`}
							>
								<Container>
									{block.general_content.heading && <h2 className='mb-5 font-semibold xl:mb-8'>{block.general_content.heading}</h2>}
									{block.general_content.subheading && <h3>{block.general_content.subheading}</h3>}
									{block.general_content.description && <div dangerouslySetInnerHTML={{ __html: block.general_content.description }} />}
									{isValidArray(block.general_content.ctas) &&
										block.general_content.ctas.map((link, index) => (
											<CTA
												data={link}
												key={index}
											/>
										))}
								</Container>
							</div>
						);
					}

					if (isValidArray(block.link_group?.group)) {
						return (
							<div
								key={index}
								className='bg-neutral-25 py-8 md:py-12 xl:py-16'
							>
								<Container>
									{block.link_group.heading && <h2 className='mb-5 text-center font-semibold xl:mb-8'>{block.link_group.heading}</h2>}

									<List
										bullets='none'
										className='block grid-cols-2 gap-5 md:grid lg:gap-7'
									>
										{isValidArray(block.link_group.group) &&
											block.link_group.group.map((tab, index) => (
												<li
													key={index}
													className='mb-5 md:mb-0'
												>
													<button
														onClick={() => onTabClick(index)}
														className={`relative flex h-full w-full items-center border border-neutral-100 py-5 pl-8 pr-20 text-xl font-semibold !leading-tight shadow-[0_2px_4px_0_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0_3px_12px_0_rgba(0,0,0,0.05)] md:h-24 md:py-0 md:text-2xl lg:h-32 lg:pl-10 lg:pr-24 xl:text-3xl ${activeTab == index ? 'border-2 !border-black bg-primary' : activeTab != index && activeTab != null ? 'bg-neutral-400' : 'bg-white'}`}
													>
														{tab.heading}
														<Icon
															name={index == activeTab ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
															className='absolute right-5 top-1/2 block -translate-y-1/2 lg:right-9'
														/>
													</button>

													{!isTablet && activeTab != null && activeTab == index && isValidArray(tab.group) && renderSubLinks(block)}
												</li>
											))}
									</List>

									{isTablet && activeTab != null && block.link_group?.group?.[activeTab] && renderSubLinks(block)}
								</Container>
							</div>
						);
					}

					if (block.faq != null && isValidArray(block.faq.group)) {
						if (block.faq.group.length == 1 && !dynamic.faq) return null;

						const faqArr = (side) => {
							if (block.faq.group.length === 1) {
								return block.faq.group;
							} else {
								return block.faq.group.filter((item, index) => (side === 'left' ? index % 2 === 0 : index % 2 !== 0));
							}
						};

						return (
							<div
								key={index}
								className='my-12 lg:my-20'
							>
								<Container>
									{block.faq.heading && <h2 className='mb-5 text-center xl:mb-8'>{block.faq.heading}</h2>}
									<Row>
										<Column
											xs={12}
											lg={block.faq.group.length !== 1 ? 6 : null}
										>
											{faqArr('left').map((group, index) => renderFaqColumn(group, index, block.faq.group.length === 1))}
										</Column>

										{block.faq.group.length > 1 && (
											<Column
												xs={12}
												lg={6}
											>
												{faqArr('right').map((group, index) => renderFaqColumn(group, index, block.faq.group.length === 1))}
											</Column>
										)}
									</Row>
									{isValidArray(block.faq.ctas) && (
										<div className='text-center'>
											<CTA data={block.faq.ctas[0]} />
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (isValidArray(block.all_dynamic?.dynamic)) {
						return (
							<DynamicComponent
								key={index}
								data={block.all_dynamic}
							/>
						);
					}

					if (isValidArray(block.content_with_module?.group)) {
						return (
							<div
								key={index}
								className='my-8 lg:my-12'
							>
								<Container>
									{block.content_with_module.subheading && <h2 dangerouslySetInnerHTML={{ __html: block.content_with_module.subheading }} />}

									<Row verticalGutters>
										{block.content_with_module.group.map((item, index) => (
											<Column
												key={index}
												xs={item.column_mobile}
												lg={item.column_desktop}
											>
												<Row verticalGutters>
													{isValidArray(item.image) && (
														<Column
															xs={12}
															sm={6}
														>
															{(item.modal_cta && (
																<button
																	onClick={(e) => onModalCtaClick(e, item.image[0], item.modal_heading, 'cta')}
																	className='block text-left text-blue'
																	aria-label='Modal CTA'
																>
																	<Image
																		data={item.image[0]}
																		className='border border-black'
																	/>
																	<div className='mt-2.5 flex items-center font-bold'>
																		<span className='material-symbols-sharp mr-1'>add_circle_outline</span>
																		<span className='hover:underline'>{item.modal_cta}</span>
																	</div>
																</button>
															)) || (
																<Image
																	data={item.image[0]}
																	className='border border-black'
																/>
															)}
														</Column>
													)}
													<Column
														xs={12}
														sm={6}
													>
														{item.heading && <h3 className='mb-3 border-b-2 border-primary pb-3 text-xl font-normal uppercase'>{item.heading}</h3>}
														{item.description && <div dangerouslySetInnerHTML={{ __html: item.description }} />}
													</Column>
												</Row>
											</Column>
										))}
									</Row>
								</Container>

								<Modal
									isOpen={modal.open === 'cta'}
									onClose={onModalCloseClick}
									width={600}
								>
									{modal.heading && <Modal.Header>{modal.heading}</Modal.Header>}
									{modal.image && (
										<Modal.Content className='text-center'>
											<Image data={modal.image} />
										</Modal.Content>
									)}
									<Modal.Footer className='text-right'>
										<Button
											color='primary'
											onClick={onModalCloseClick}
										>
											Close
										</Button>
									</Modal.Footer>
								</Modal>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								id='js-supportCards'
								data={block.cards}
								key={index}
							/>
						);
					}

					if (isValidArray(block.cta_ribbon?.cta_ribbon)) {
						return (
							<Container
								key={index}
								className='my-8 md:my-10'
							>
								<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
							</Container>
						);
					}
				})}

			<VisualJourneyModal
				isOpen={modal.open === 'service'}
				onClose={onModalCloseClick}
				redirect='/support/request-visit/'
			/>
		</Layout>
	);
};

Support.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Support;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackSupport.seo}
	/>
);

export const query = graphql`
	query ($uid: String!) {
		contentstackSupport(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			hero_heading
			hero_subheading
			search {
				search_heading
				results_heading
				back
				support_help
				related_search
			}
			top_content {
				heading
				description
				ctas {
					...CTA
				}
				image {
					...Image
					image {
						gatsbyImageData(quality: 90)
					}
				}
			}
			page_blocks {
				columns {
					background
					heading
					column {
						background
						theme
						justify
						column_desktop
						column_mobile
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				forms {
					form_position
					heading
					subheading
					form_type
					aside_content {
						position
						heading
						description
						subheading
					}
				}
				image_with_content {
					heading
					column_desktop
					column_mobile
					image_position
					style
					group {
						file {
							url
							gatsbyImageData(quality: 90)
						}
						body
						cta {
							...CTA
						}
					}
				}
				general_content {
					align
					background_color
					heading
					subheading
					description
					ctas {
						...CTA
					}
				}
				link_group {
					heading
					group {
						heading
						group {
							link {
								title
								href
							}
							description
						}
					}
				}
				faq {
					heading
					group {
						subheading
						faqs {
							question
							answer
						}
						dynamic {
							...Dynamic
						}
						ctas {
							...CTA
						}
					}
					ctas {
						...CTA
					}
				}
				all_dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				content_with_module {
					subheading
					group {
						column_desktop
						column_mobile
						modal_cta
						modal_heading
						heading
						description
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
					}
				}
				cards {
					...CardSection
				}
				cta_ribbon {
					cta_ribbon {
						...CTARibbon
					}
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
